// Centralized case filtering utilities
import { isCaseSettled } from '../components/CaseReview/SummaryCaseTable/utils/helpers.js';
import logger from './logger';

/**
 * Determines if a case is settled based on determination actions and offer equality
 * @param {Object|Array} caseItem - The case object or array of cases
 * @returns {boolean|Array} - True if the case is settled, or array of boolean results for arrays
 */
export const isCaseSettledUtil = (caseItem) => {
  return isCaseSettled(caseItem);
};

/**
 * Determines if a case is active (not settled and filed)
 * @param {Object} caseItem - The case object
 * @returns {boolean} - True if the case is active
 */
export const isCaseActive = (caseItem) => {
  const isSettled = isCaseSettledUtil(caseItem);
  const isFiled = caseItem.BARFiled === 1 || caseItem.SCARFiled !== 0;
  return !isSettled && isFiled;
};

/**
 * Determines if a case matches the BAR stage filter
 * @param {Object} caseItem - The case object
 * @returns {boolean} - True if case is BAR-filed
 */
export const isBARCase = (caseItem) => {
  return caseItem.BARFiled === 1 && caseItem.SCARFiled !== 1;
};

/**
 * Determines if a case matches the SCAR stage filter
 * @param {Object} caseItem - The case object
 * @returns {boolean} - True if case is SCAR-filed
 */
export const isSCARCase = (caseItem) => {
  // Only treat explicitly filed SCAR cases as SCAR.
  // Null/undefined (not filed yet) should NOT show in the SCAR filter.
  return caseItem.SCARFiled === 1;
};

/**
 * Filters cases based on stage criteria
 * @param {Array} cases - Array of case objects
 * @param {string} activeStage - The stage filter ('BAR', 'SCAR', 'all')
 * @returns {Array} - Filtered array of cases
 */
export const filterCasesByStage = (cases, activeStage) => {
  if (!cases || !Array.isArray(cases)) return [];
  
  return cases.filter(caseItem => {
    if (activeStage === 'BAR') {
      return isBARCase(caseItem);
    } else if (activeStage === 'SCAR') {
      return isSCARCase(caseItem);
    } else if (activeStage === 'all') {
      return true; // Include all cases
    }
    return false;
  });
};

/**
 * Filters cases based on resolution status
 * @param {Array} cases - Array of case objects
 * @param {string} resolutionFilter - The resolution filter ('active', 'settled', 'all')
 * @returns {Array} - Filtered array of cases
 */
export const filterCasesByResolution = (cases, resolutionFilter) => {
  if (!cases || !Array.isArray(cases)) return [];
  
  return cases.filter(caseItem => {
    if (resolutionFilter === 'active') {
      return isCaseActive(caseItem);
    } else if (resolutionFilter === 'settled') {
      return isCaseSettledUtil(caseItem);
    } else if (resolutionFilter === 'all') {
      return true; // Include all cases
    }
    return false;
  });
};

/**
 * Comprehensive case filtering with multiple criteria
 * @param {Array} cases - Array of case objects
 * @param {Object} filters - Filter criteria object
 * @param {string} filters.activeStage - Stage filter ('BAR', 'SCAR', 'all')
 * @param {string} filters.resolutionFilter - Resolution filter ('active', 'settled', 'all')
 * @param {boolean} filters.townView - Whether in town view mode
 * @returns {Array} - Filtered array of cases
 */
export const filterCases = (cases, filters = {}) => {
  if (!cases || !Array.isArray(cases)) return [];
  
  const { activeStage, resolutionFilter, townView = false } = filters;
  
  let filteredCases = cases;
  
  // Apply stage filtering (only in townView)
  if (townView && activeStage) {
    filteredCases = filterCasesByStage(filteredCases, activeStage);
  }
  
  // Apply resolution filtering (only in townView)
  if (townView && resolutionFilter && resolutionFilter !== 'all') {
    filteredCases = filterCasesByResolution(filteredCases, resolutionFilter);
  }
  
  return filteredCases;
};

/**
 * Calculates case statistics for a given set of cases
 * @param {Array} cases - Array of case objects
 * @param {Object} filters - Optional filter criteria
 * @returns {Object} - Statistics object with total, active, settled counts
 */
export const calculateCaseStatistics = (cases, filters = {}) => {
  if (!cases || !Array.isArray(cases)) {
    return { total: 0, active: 0, settled: 0 };
  }
  
  // Apply stage filtering only for statistics
  const { activeStage } = filters;
  const stageFilteredCases = filterCasesByStage(cases, activeStage);
  
  const total = stageFilteredCases.length;
  logger.log('stats form calc case stats');
  const active = stageFilteredCases.filter(isCaseActive).length;
  const settledStatuses = isCaseSettled(stageFilteredCases);
  logger.log('settledStatuses', settledStatuses);
  const settled = settledStatuses.filter(Boolean).length;
  logger.log('settled', settled);
  
  return { total, active, settled };
};


/**
 * Gets the display name for a stage filter
 * @param {string} activeStage - The stage filter value
 * @returns {string} - Display name for the stage
 */
export const getStageDisplayName = (activeStage) => {
  switch (activeStage) {
    case 'BAR':
      return 'BAR Cases';
    case 'SCAR':
      return 'SCAR Cases';
    case 'all':
      return 'All Cases';
    default:
      return 'All Cases';
  }
};

/**
 * Gets the display name for a resolution filter
 * @param {string} resolutionFilter - The resolution filter value
 * @returns {string} - Display name for the resolution
 */
export const getResolutionDisplayName = (resolutionFilter) => {
  switch (resolutionFilter) {
    case 'active':
      return 'Active';
    case 'settled':
      return 'Settled';
    case 'all':
      return 'All Cases';
    default:
      return 'All Cases';
  }
}; 